import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useReservationInvalidation } from "../../app/hooks/useFamilyWebSocket";
import { Icon } from "../../components/Icon";
import { ApiError } from "../../lib/api-client";
import { getMyFamilies } from "../../lib/api-family";
import {
  cancelReservation,
  purchaseReservation,
  reserveWish,
} from "../../lib/api-reservation";
import { getMemberWishes, reorderWishes } from "../../lib/api-wish";
import type {
  ReservationAction,
  ReservationMutation,
} from "../../types/reservation";
import type {
  NonOwnerWish,
  OwnerWish,
  WishListItem,
  WishPriority,
  WishStatus,
} from "../../types/wish";
import { DeleteWishConfirm } from "./DeleteWishConfirm";
import { EditWishModal } from "./EditWishModal";
import { ReservationActionConfirm } from "./ReservationActionConfirm";
import { WishCard } from "./WishCard";

type WishlistState =
  | { status: "loading" }
  | { status: "loaded"; wishes: WishListItem[]; isOwner: boolean }
  | { status: "error" };

interface SelectedReservationAction {
  wish: NonOwnerWish;
  action: ReservationAction;
}

interface ReorderAttempt {
  before: WishListItem[];
  after: WishListItem[];
  wishId: string;
  title: string;
}

type WishFilters = {
  priority: "all" | WishPriority;
  eventId: string;
  status: "all" | WishStatus;
};

const priorityFilterLabels: Record<WishFilters["priority"], string> = {
  all: "Любой приоритет",
  high: "Высокий",
  medium: "Средний",
  low: "Низкий",
};

const statusFilterLabels: Record<WishFilters["status"], string> = {
  all: "Любая доступность",
  free: "Только свободные",
  reserved: "Только забронированные",
  purchased: "Только купленные",
};

const defaultFilters: WishFilters = {
  priority: "all",
  eventId: "all",
  status: "all",
};

function filterWishes(
  wishes: WishListItem[],
  filters: WishFilters,
  isOwner: boolean,
): WishListItem[] {
  return wishes.filter((wish) => {
    if (filters.priority !== "all" && wish.priority !== filters.priority) {
      return false;
    }
    if (filters.eventId === "none" && wish.event_id) {
      return false;
    }
    if (filters.eventId !== "all" && filters.eventId !== "none" && wish.event_id !== filters.eventId) {
      return false;
    }
    return isOwner || filters.status === "all" || ("status" in wish && wish.status === filters.status);
  });
}

function moveWish(wishes: WishListItem[], wishId: string, targetId: string) {
  const from = wishes.findIndex((wish) => wish.id === wishId);
  const to = wishes.findIndex((wish) => wish.id === targetId);
  if (from < 0 || to < 0 || from === to) return wishes;
  const next = [...wishes];
  const moved = next[from];
  if (!moved) return wishes;
  next.splice(from, 1);
  next.splice(to, 0, moved);
  return next.map((wish, index) => ({ ...wish, sort_order: index + 1 }));
}

export function WishlistPage() {
  const { memberId } = useParams<{ memberId: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<WishlistState>({ status: "loading" });
  const [editingWish, setEditingWish] = useState<OwnerWish | null>(null);
  const [deletingWish, setDeletingWish] = useState<OwnerWish | null>(null);
  const [reservationAction, setReservationAction] = useState<SelectedReservationAction | null>(null);
  const [reservationPending, setReservationPending] = useState(false);
  const [reservationError, setReservationError] = useState<string | null>(null);
  const [reservationNotice, setReservationNotice] = useState<string | null>(null);
  const [reorderPending, setReorderPending] = useState(false);
  const [reorderNotice, setReorderNotice] = useState("");
  const [failedReorder, setFailedReorder] = useState<ReorderAttempt | null>(null);
  const [filters, setFilters] = useState<WishFilters>(defaultFilters);
  const requestIDRef = useRef(0);
  const dragRef = useRef<(ReorderAttempt & { overId: string }) | undefined>(undefined);

  const loadWishes = useCallback(async () => {
    if (!memberId) return;
    setFilters(defaultFilters);
    const requestID = ++requestIDRef.current;
    setState({ status: "loading" });
    try {
      const [wishes, memberships] = await Promise.all([
        getMemberWishes(memberId),
        getMyFamilies(),
      ]);
      if (requestID === requestIDRef.current) {
        setState({
          status: "loaded",
          wishes,
          isOwner: memberships.some((family) => family.family_member_id === memberId),
        });
      }
    } catch {
      if (requestID === requestIDRef.current) setState({ status: "error" });
    }
  }, [memberId]);

  useEffect(() => {
    void loadWishes();
    return () => {
      requestIDRef.current++;
    };
  }, [loadWishes]);

  const refetchWishes = useCallback(async () => {
    if (!memberId) return;
    const requestID = ++requestIDRef.current;
    const wishes = await getMemberWishes(memberId);
    if (requestID === requestIDRef.current) {
      setState((current) =>
        current.status === "loaded" ? { ...current, wishes } : current,
      );
    }
  }, [memberId]);

  useReservationInvalidation((signal) => {
    if (state.status !== "loaded") {
      void loadWishes();
      return;
    }
    if (state.isOwner) return;
    if (
      signal.reason === "reconnect" ||
      state.wishes.some((wish) => wish.id === signal.event.wish_id)
    ) {
      void refetchWishes().catch(() => undefined);
    }
  }, Boolean(memberId));

  if (!memberId) {
    return <Navigate to="/" replace />;
  }

  const addWish = () => navigate(`/members/${memberId}/wishes/new`);

  const applyReservationMutation = (mutation: ReservationMutation) => {
    setState((current) =>
      current.status === "loaded"
        ? {
            ...current,
            wishes: current.wishes.map((wish) =>
              wish.id === mutation.wish_id && "status" in wish
                ? {
                    ...wish,
                    status: mutation.wish_status,
                    my_reservation_id:
                      mutation.status === "reserved"
                        ? mutation.reservation_id
                        : undefined,
                    can_reserve: mutation.wish_status === "free",
                    can_cancel: mutation.can_cancel,
                    can_purchase: mutation.can_purchase,
                  }
                : wish,
            ),
          }
        : current,
    );
  };

  const openReservationAction = (wish: NonOwnerWish, action: ReservationAction) => {
    setReservationError(null);
    setReservationNotice(null);
    setReservationAction({ wish, action });
  };

  const confirmReservationAction = async () => {
    if (!reservationAction) return;
    const { wish, action } = reservationAction;
    setReservationPending(true);
    setReservationError(null);
    try {
      let mutation: ReservationMutation;
      if (action === "reserve") {
        mutation = await reserveWish(wish.id);
      } else if (!wish.my_reservation_id) {
        throw new Error("reservation capability has no id");
      } else if (action === "cancel") {
        mutation = await cancelReservation(wish.my_reservation_id);
      } else {
        mutation = await purchaseReservation(wish.my_reservation_id);
      }
      applyReservationMutation(mutation);
      setReservationAction(null);
      setReservationNotice(
        action === "reserve"
          ? "Подарок забронирован."
          : action === "cancel"
            ? "Бронь отменена."
            : "Подарок отмечен купленным.",
      );
      try {
        await refetchWishes();
      } catch {
        setReservationNotice("Изменение сохранено, но обновить список не удалось.");
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        setReservationAction(null);
        setReservationNotice("Состояние подарка уже изменилось. Список обновлён.");
        try {
          await refetchWishes();
        } catch {
          setReservationNotice("Состояние подарка изменилось. Обновите список ещё раз.");
        }
      } else if (error instanceof ApiError && error.code === "FORBIDDEN") {
        setReservationError("Этой бронью уже нельзя управлять.");
      } else {
        setReservationError("Не удалось сохранить изменение. Попробуйте ещё раз.");
      }
    } finally {
      setReservationPending(false);
    }
  };

  const handleUpdated = (updated: OwnerWish) => {
    setState((current) =>
      current.status === "loaded"
        ? {
            status: "loaded",
            isOwner: current.isOwner,
            wishes: current.wishes.map((wish) =>
              wish.id === updated.id ? updated : wish,
            ),
          }
        : current,
    );
    setEditingWish(null);
  };

  const handleDeleted = (id: string) => {
    setState((current) =>
      current.status === "loaded"
        ? {
            status: "loaded",
            isOwner: current.isOwner,
            wishes: current.wishes.filter((wish) => wish.id !== id),
          }
        : current,
    );
    setDeletingWish(null);
  };

  const ownerView = state.status === "loaded" && state.isOwner;
  const visibleWishes = state.status === "loaded"
    ? filterWishes(state.wishes, filters, state.isOwner)
    : [];
  const eventOptions = state.status === "loaded"
    ? Array.from(
        new Map(
          state.wishes
            .filter((wish) => wish.event)
            .map((wish) => [wish.event!.id, wish.event!.title]),
        ),
      )
    : [];
  const filtersActive = filters.priority !== "all" || filters.eventId !== "all" || filters.status !== "all";

  const persistOrder = async (attempt: ReorderAttempt) => {
    setReorderPending(true);
    setFailedReorder(null);
    const position = attempt.after.findIndex((wish) => wish.id === attempt.wishId) + 1;
    setReorderNotice(`«${attempt.title}»: позиция ${position} из ${attempt.after.length}. Сохраняем.`);
    try {
      await reorderWishes(memberId, attempt.after.map((wish) => wish.id));
      setReorderNotice(`Порядок сохранён. «${attempt.title}»: позиция ${position} из ${attempt.after.length}.`);
    } catch {
      setState((current) => current.status === "loaded" ? { ...current, wishes: attempt.before } : current);
      setFailedReorder(attempt);
      setReorderNotice("Не удалось сохранить порядок. Предыдущий порядок восстановлен.");
    } finally {
      setReorderPending(false);
    }
  };

  const moveByKeyboard = (wishId: string, direction: -1 | 1) => {
    if (state.status !== "loaded" || reorderPending) return;
    const index = state.wishes.findIndex((wish) => wish.id === wishId);
    const wish = state.wishes[index];
    const target = state.wishes[index + direction];
    if (!wish || !target) return;
    const after = moveWish(state.wishes, wishId, target.id);
    const attempt = { before: state.wishes, after, wishId, title: wish.title };
    setState({ ...state, wishes: after });
    void persistOrder(attempt);
  };

  const startDrag = (wishId: string, event: React.PointerEvent<HTMLButtonElement>) => {
    if (state.status !== "loaded" || reorderPending) return;
    const wish = state.wishes.find((item) => item.id === wishId);
    if (!wish) return;
    try {
      event.currentTarget.setPointerCapture?.(event.pointerId);
    } catch {
      // Synthetic pointer events do not represent an active browser pointer.
    }
    dragRef.current = { wishId, overId: wishId, before: state.wishes, after: state.wishes, title: wish.title };
  };

  const dragOver = (event: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    const targetId = document.elementFromPoint?.(event.clientX, event.clientY)
      ?.closest<HTMLElement>("[data-wish-id]")?.dataset.wishId;
    if (!drag || !targetId || targetId === drag.overId) return;
    drag.overId = targetId;
    const after = moveWish(drag.after, drag.wishId, targetId);
    if (after === drag.after) return;
    drag.after = after;
    setState((current) => current.status === "loaded" ? { ...current, wishes: after } : current);
  };

  const finishDrag = () => {
    const drag = dragRef.current;
    dragRef.current = undefined;
    if (drag && drag.after !== drag.before) void persistOrder(drag);
  };

  const cancelDrag = () => {
    const drag = dragRef.current;
    dragRef.current = undefined;
    if (drag) setState((current) => current.status === "loaded" ? { ...current, wishes: drag.before } : current);
  };

  return (
    <div className="flex flex-1 flex-col px-5 pb-8 pt-5 sm:px-8 lg:px-10 lg:py-10 xl:px-14">
      <header className="mb-7 flex items-center justify-between gap-4 lg:mb-10">
        <div>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="mb-4 flex cursor-pointer items-center gap-1 text-xs font-semibold text-[#77776F] hover:text-[#171714]"
          >
            <Icon name="arrow-left" size={16} />
            К семье
          </button>
          <p className="mb-1 text-[11px] font-bold uppercase tracking-[0.18em] text-[#FF7A00]">
            {ownerView ? "Личный список" : "Список участника"}
          </p>
          <h1 className="text-[32px] font-extrabold leading-none tracking-[-0.055em] text-[#171714] lg:text-[46px]">
            {ownerView ? "Мои желания" : "Список желаний"}
          </h1>
        </div>
        {ownerView && <button
          type="button"
          onClick={addWish}
          className="flex h-12 w-12 shrink-0 cursor-pointer items-center justify-center rounded-full bg-[#FF7A00] text-white shadow-[0_10px_24px_rgba(255,122,0,0.25)] transition-transform hover:scale-105"
          aria-label="Добавить желание"
        >
          <Icon name="plus" size={22} />
        </button>}
      </header>

      {state.status === "loading" && (
        <div className="flex flex-1 items-center justify-center py-20" aria-label="Загрузка желаний">
          <div className="h-9 w-9 animate-spin rounded-full border-[3px] border-[#FF7A00] border-t-transparent" />
        </div>
      )}

      {state.status === "error" && (
        <div className="flex flex-1 flex-col items-center justify-center rounded-[28px] bg-white px-7 py-16 text-center shadow-[0_14px_36px_rgba(35,33,25,0.07)]">
          <h2 className="text-2xl font-extrabold tracking-[-0.04em] text-[#171714]">
            Не удалось загрузить желания
          </h2>
          <p className="mt-2 text-sm text-[#77776F]">Проверьте подключение и попробуйте снова.</p>
          <button
            type="button"
            onClick={loadWishes}
            className="mt-6 cursor-pointer rounded-full bg-[#171714] px-7 py-3 text-sm font-semibold text-white"
          >
            Повторить
          </button>
        </div>
      )}

      {state.status === "loaded" && state.wishes.length === 0 && (
        <div className="flex flex-1 flex-col items-center justify-center rounded-[28px] bg-white px-7 py-16 text-center shadow-[0_14px_36px_rgba(35,33,25,0.07)]">
          <div className="flex h-20 w-20 items-center justify-center rounded-[26px] bg-[#FFF0DF] text-[#FF7A00]">
            <Icon name="gift" size={36} />
          </div>
          <h2 className="mt-6 text-2xl font-extrabold tracking-[-0.04em] text-[#171714]">
            Список пока пуст
          </h2>
          <p className="mt-2 max-w-sm text-sm leading-6 text-[#77776F]">
            {state.isOwner
              ? "Добавьте первое желание — со ссылкой на товар или просто идею подарка."
              : "У этого участника пока нет желаний."}
          </p>
          {state.isOwner && <button
            type="button"
            onClick={addWish}
            className="mt-7 cursor-pointer rounded-full bg-[#FF7A00] px-8 py-3.5 text-sm font-bold text-white shadow-[0_10px_24px_rgba(255,122,0,0.25)]"
          >
            Добавить первое желание
          </button>}
        </div>
      )}

      {reservationNotice && (
        <div role="status" aria-live="polite" className="mb-4 rounded-2xl border border-[#F4C48F] bg-[#FFF7ED] px-4 py-3 text-sm font-semibold text-[#9A4900]">
          {reservationNotice}
        </div>
      )}

      <p role="status" aria-live="polite" className="sr-only">{reorderNotice}</p>
      {failedReorder && (
        <div role="alert" className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#F5B8B3] bg-[#FEF2F2] px-4 py-3 text-sm font-semibold text-[#9F2F27]">
          <span>Не удалось сохранить порядок. Предыдущий порядок восстановлен.</span>
          <button
            type="button"
            disabled={reorderPending}
            onClick={() => {
              setState((current) => current.status === "loaded" ? { ...current, wishes: failedReorder.after } : current);
              void persistOrder(failedReorder);
            }}
            className="min-h-11 cursor-pointer rounded-xl bg-[#171714] px-4 py-2 text-sm font-bold text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#171714] disabled:cursor-wait disabled:opacity-60"
          >
            Повторить сохранение
          </button>
        </div>
      )}

      {state.status === "loaded" && state.wishes.length > 0 && (
        <section className="mb-5 rounded-[24px] border border-black/[0.05] bg-white p-4 shadow-[0_10px_28px_rgba(35,33,25,0.05)]" aria-labelledby="wishlist-filters-heading">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 id="wishlist-filters-heading" className="text-sm font-extrabold text-[#171714]">Фильтры списка</h2>
            {filtersActive && (
              <button
                type="button"
                onClick={() => setFilters(defaultFilters)}
                className="rounded-xl px-3 py-2 text-sm font-semibold text-[#C45D00] hover:bg-[#FFF0DF] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C45D00]"
              >
                Сбросить фильтры
              </button>
            )}
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <label className="flex flex-col gap-1.5 text-xs font-semibold text-[#77776F]" htmlFor="wishlist-priority-filter">
              Приоритет
              <select
                id="wishlist-priority-filter"
                value={filters.priority}
                onChange={(event) => setFilters((current) => ({ ...current, priority: event.target.value as WishFilters["priority"] }))}
                className="h-11 rounded-xl border border-[#D9D7CF] bg-white px-3 text-sm font-semibold text-[#171714] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#171714]"
              >
                {(Object.keys(priorityFilterLabels) as WishFilters["priority"][]).map((value) => (
                  <option key={value} value={value}>{priorityFilterLabels[value]}</option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1.5 text-xs font-semibold text-[#77776F]" htmlFor="wishlist-event-filter">
              Событие
              <select
                id="wishlist-event-filter"
                value={filters.eventId}
                onChange={(event) => setFilters((current) => ({ ...current, eventId: event.target.value }))}
                className="h-11 rounded-xl border border-[#D9D7CF] bg-white px-3 text-sm font-semibold text-[#171714] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#171714]"
              >
                <option value="all">Любое событие</option>
                <option value="none">Без события</option>
                {eventOptions.map(([id, title]) => <option key={id} value={id}>Событие: {title}</option>)}
              </select>
            </label>
            {!state.isOwner && (
              <label className="flex flex-col gap-1.5 text-xs font-semibold text-[#77776F]" htmlFor="wishlist-status-filter">
                Доступность
                <select
                  id="wishlist-status-filter"
                  value={filters.status}
                  onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value as WishFilters["status"] }))}
                  className="h-11 rounded-xl border border-[#D9D7CF] bg-white px-3 text-sm font-semibold text-[#171714] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#171714]"
                >
                  {(Object.keys(statusFilterLabels) as WishFilters["status"][]).map((value) => (
                    <option key={value} value={value}>{statusFilterLabels[value]}</option>
                  ))}
                </select>
              </label>
            )}
          </div>
          <p className="mt-3 text-xs text-[#77776F]" aria-live="polite">
            Показано желаний: {visibleWishes.length} из {state.wishes.length}
          </p>
        </section>
      )}

      {state.status === "loaded" && state.wishes.length > 0 && visibleWishes.length === 0 && (
        <div className="flex flex-col items-center rounded-[28px] bg-white px-7 py-12 text-center shadow-[0_14px_36px_rgba(35,33,25,0.07)]">
          <h2 className="text-2xl font-extrabold tracking-[-0.04em] text-[#171714]">Ничего не найдено</h2>
          <p className="mt-2 max-w-sm text-sm leading-6 text-[#77776F]">Измените фильтры или сбросьте их, чтобы увидеть весь список.</p>
          <button
            type="button"
            onClick={() => setFilters(defaultFilters)}
            className="mt-6 rounded-full bg-[#171714] px-6 py-3 text-sm font-semibold text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#171714]"
          >
            Показать весь список
          </button>
        </div>
      )}

      {state.status === "loaded" && visibleWishes.length > 0 && (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {visibleWishes.map((wish, index) => (
            <WishCard
              key={wish.id}
              wish={wish}
              onEdit={setEditingWish}
              onDelete={setDeletingWish}
              onReservationAction={openReservationAction}
              reservationPending={reservationPending && reservationAction?.wish.id === wish.id}
              reorder={state.isOwner && !filtersActive && state.wishes.length > 1 ? {
                position: index + 1,
                total: state.wishes.length,
                pending: reorderPending,
                onMove: (direction) => moveByKeyboard(wish.id, direction),
                onPointerDown: (event) => startDrag(wish.id, event),
                onPointerMove: dragOver,
                onPointerUp: finishDrag,
                onPointerCancel: cancelDrag,
              } : undefined}
            />
          ))}
        </div>
      )}

      {editingWish && (
        <EditWishModal
          key={editingWish.id}
          wish={editingWish}
          onClose={() => setEditingWish(null)}
          onUpdated={handleUpdated}
        />
      )}
      {deletingWish && (
        <DeleteWishConfirm
          key={deletingWish.id}
          wish={deletingWish}
          onClose={() => setDeletingWish(null)}
          onDeleted={handleDeleted}
        />
      )}
      {reservationAction && (
        <ReservationActionConfirm
          action={reservationAction.action}
          wishTitle={reservationAction.wish.title}
          pending={reservationPending}
          error={reservationError}
          onClose={() => {
            setReservationAction(null);
            setReservationError(null);
          }}
          onConfirm={() => void confirmReservationAction()}
        />
      )}
    </div>
  );
}
